import React, { useState, useEffect, useRef } from "react";
import TimeLinePaciente from "./TimeLinePaciente";
import { postConsulta } from "./ConsultaService";
import { alterarStatusAgendamento } from "./AgendaService";
import Graficos from "./Graficos";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBold,
    faItalic,
    faUnderline,
    faListUl,
    faListOl,
} from "@fortawesome/free-solid-svg-icons";

const FichaDeAtendimentoModal = ({
    observacao,
    tipoConsulta,
    idAgenda,
    statusAgenda,
    token,
    authResponseDTO,
    funcionarioDoEventoSelecionado,
    consultasPorIdPaciente,
    paciente,
    isModalOpen,
    onCloseModal,
    consultasParaGraficosPorIdPaciente,
}) => {
    const classesSublistaResumo =
        "mt-1 ml-0 list-disc list-outside pl-6 space-y-1";
    const [formData, setFormData] = useState({
        paciente: paciente,
        anamnese: {
            queixaPrincipal: "",
            historiaDaMolestiaAtual: "",
            revisaoDeSistemas: "",
            historiaPatologicaPregressa: "",
            historiaFamiliar: "",
            historiaSocial: "",
        },
        exameFisico: {
            exameFisico: "",
            peso: "",
            altura: "",
            perimetroCefalico: "",
        },
        exameComplementar: {
            exameComplementar: "",
        },
        conduta: {
            conduta: "",
        },
        usuario: authResponseDTO,
        resumoCompletoDoHistorico: "",
    });
    const [resumoEmTelaCheia, setResumoEmTelaCheia] = useState(false);
    const resumoCompletoRef = useRef(null);
    const resumoAtualizadoViaCopiarRef = useRef(false);

    useEffect(() => {
        setFormData({
            ...formData,
            paciente: paciente,
            usuario: authResponseDTO,
        });
    }, [paciente, authResponseDTO, tipoConsulta]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        const elementoResumo = resumoCompletoRef.current;
        if (!elementoResumo) {
            return;
        }

        const html = formData.resumoCompletoDoHistorico || "";
        const precisaSincronizar =
            resumoAtualizadoViaCopiarRef.current ||
            (elementoResumo.innerHTML === "" && html !== "");

        if (!precisaSincronizar) {
            return;
        }

        elementoResumo.innerHTML = html;
        resumoAtualizadoViaCopiarRef.current = false;
    }, [formData.resumoCompletoDoHistorico, isModalOpen]);

    const handleAnamneseChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            anamnese: {
                ...formData.anamnese,
                [name]: value,
            },
        });
    };

    const handleExameFisicoChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            exameFisico: {
                ...formData.exameFisico,
                [name]: value,
            },
        });
    };

    const handleExameComplementarChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            exameComplementar: {
                ...formData.exameComplementar,
                [name]: value,
            },
        });
    };

    const handleCondutaChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            conduta: {
                ...formData.conduta,
                [name]: value,
            },
        });
    };

    const handleResumoHtmlInput = (event) => {
        const html = event.currentTarget.innerHTML;
        setFormData((prevState) => ({
            ...prevState,
            resumoCompletoDoHistorico: html,
        }));
    };

    const sincronizarResumoHtml = () => {
        const elementoResumo = resumoCompletoRef.current;
        if (!elementoResumo) {
            return;
        }

        setFormData((prevState) => ({
            ...prevState,
            resumoCompletoDoHistorico: elementoResumo.innerHTML,
        }));
    };

    const manterSelecaoNoResumo = () => {
        const elementoResumo = resumoCompletoRef.current;
        if (!elementoResumo) {
            return;
        }

        const selecao = window.getSelection();
        if (!selecao) {
            return;
        }

        const selecaoEstaNoResumo =
            selecao.rangeCount > 0 &&
            elementoResumo.contains(selecao.anchorNode);

        if (selecaoEstaNoResumo) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(elementoResumo);
        range.collapse(false);
        selecao.removeAllRanges();
        selecao.addRange(range);
    };

    const obterItemListaAtual = () => {
        const elementoResumo = resumoCompletoRef.current;
        const selecao = window.getSelection();
        if (!elementoResumo || !selecao || selecao.rangeCount === 0) {
            return null;
        }

        const noAtual = selecao.anchorNode;
        const elementoAtual =
            noAtual.nodeType === Node.ELEMENT_NODE
                ? noAtual
                : noAtual.parentElement;
        const itemListaAtual = elementoAtual?.closest("li");

        return elementoResumo.contains(itemListaAtual) ? itemListaAtual : null;
    };

    const posicionarCursorNoElemento = (elemento) => {
        const selecao = window.getSelection();
        if (!selecao || !elemento) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(elemento);
        range.collapse(false);
        selecao.removeAllRanges();
        selecao.addRange(range);
    };

    const adicionarSubitemCircularNoResumo = () => {
        const itemListaAtual = obterItemListaAtual();
        const listaAtual = itemListaAtual?.parentElement;

        if (!itemListaAtual || listaAtual?.tagName !== "OL") {
            return false;
        }

        let sublista = Array.from(itemListaAtual.children).find(
            (elemento) => elemento.tagName === "UL",
        );

        if (!sublista) {
            sublista = document.createElement("ul");
            itemListaAtual.appendChild(sublista);
        }
        sublista.className = classesSublistaResumo;

        const novoItem = document.createElement("li");
        novoItem.appendChild(document.createElement("br"));
        sublista.appendChild(novoItem);
        posicionarCursorNoElemento(novoItem);
        sincronizarResumoHtml();
        return true;
    };

    const adicionarProximoItemNumeradoNoResumo = () => {
        const itemListaAtual = obterItemListaAtual();
        const listaAtual = itemListaAtual?.parentElement;
        const itemNumeradoPai = listaAtual?.closest("li");
        const listaNumeradaPai = itemNumeradoPai?.parentElement;

        if (
            !itemListaAtual ||
            listaAtual?.tagName !== "UL" ||
            listaNumeradaPai?.tagName !== "OL"
        ) {
            return false;
        }

        const novoItemNumerado = document.createElement("li");
        novoItemNumerado.appendChild(document.createElement("br"));
        itemNumeradoPai.after(novoItemNumerado);

        if (itemListaAtual.textContent.trim() === "") {
            itemListaAtual.remove();
        }

        if (listaAtual.children.length === 0) {
            listaAtual.remove();
        }

        posicionarCursorNoElemento(novoItemNumerado);
        sincronizarResumoHtml();
        return true;
    };

    const handleResumoKeyDown = (event) => {
        if (event.key !== "Tab") {
            return;
        }

        event.preventDefault();

        if (event.shiftKey) {
            adicionarProximoItemNumeradoNoResumo();
            return;
        }

        adicionarSubitemCircularNoResumo();
    };

    const aplicarComandoResumo = (comando) => {
        const elementoResumo = resumoCompletoRef.current;
        if (!elementoResumo) {
            return;
        }

        elementoResumo.focus();
        manterSelecaoNoResumo();
        document.execCommand(comando, false, null);
        sincronizarResumoHtml();
    };

    const salvarConsulta = () => {
        const payload = {
            paciente: paciente,
            usuario: authResponseDTO,
            resumoCompletoDoHistorico: formData.resumoCompletoDoHistorico,
        };
        // debugger;
        postConsulta(payload, token);
        alterarStatusAgendamento(idAgenda, token, "FINALIZADA");
    };

    const formatarTexto = (valor) => {
        if (valor === null || valor === undefined) {
            return "---";
        }

        const texto = `${valor}`.trim();
        return texto === "" ? "---" : texto;
    };

    const escapeHtml = (valor) =>
        valor
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    const formatarComUnidade = (valor, unidade) => {
        if (valor === null || valor === undefined) {
            return "---";
        }

        const texto = `${valor}`.trim();
        return texto === "" ? "---" : `${texto}${unidade}`;
    };

    const decodificarHtmlEscapado = (html) => {
        const elementoTemporario = document.createElement("textarea");
        elementoTemporario.innerHTML = html;
        return elementoTemporario.value;
    };

    const normalizarResumoCompletoDoHistorico = (resumoCompletoDoHistorico) => {
        if (!resumoCompletoDoHistorico) {
            return "";
        }

        const html = `${resumoCompletoDoHistorico}`.trim();
        const pareceHtmlEscapado =
            /&lt;\/?(div|p|br|ol|ul|li|strong|b|em|i|u|span)\b/i.test(html);

        return pareceHtmlEscapado ? decodificarHtmlEscapado(html) : html;
    };

    const montarResumoCompletoHtml = (consulta) => {
        const anamnese = consulta.anamnese || {};
        const exameFisico = consulta.exameFisico || {};
        const exameComplementar = consulta.exameComplementar || {};
        const conduta = consulta.conduta || {};

        const queixaPrincipal = escapeHtml(
            formatarTexto(anamnese.queixaPrincipal),
        );
        const hma = escapeHtml(
            formatarTexto(anamnese.historiaDaMolestiaAtual),
        );
        const revisaoSistemas = escapeHtml(
            formatarTexto(anamnese.revisaoDeSistemas),
        );
        const historiaPatologicaPregressa = escapeHtml(
            formatarTexto(anamnese.historiaPatologicaPregressa),
        );
        const historiaFamiliar = escapeHtml(
            formatarTexto(anamnese.historiaFamiliar),
        );
        const historiaSocial = escapeHtml(
            formatarTexto(anamnese.historiaSocial),
        );
        const descricaoExame = escapeHtml(
            formatarTexto(exameFisico.exameFisico),
        );
        const altura = escapeHtml(formatarComUnidade(exameFisico.altura, "cm"));
        const peso = escapeHtml(formatarComUnidade(exameFisico.peso, "kg"));
        const perimetroCefalico = escapeHtml(
            formatarComUnidade(exameFisico.perimetroCefalico, "cm"),
        );
        const exameComplementarDescricao = escapeHtml(
            formatarTexto(exameComplementar.exameComplementar),
        );
        const condutaDescricao = escapeHtml(formatarTexto(conduta.conduta));

        return [
            '<ol class="space-y-4 text-gray-500 list-decimal list-inside dark:text-gray-400">',
            "\t<li>Anamnese",
            '\t\t<ul class="pl-5 mt-2 space-y-1 list-disc list-inside">',
            `\t\t\t<li><strong>Queixa principal: </strong>${queixaPrincipal}</li>`,
            `\t\t\t<li><strong>História da Moléstia Atual (HMA): </strong>${hma}</li>`,
            `\t\t\t<li><strong>Revisão de Sistemas: </strong>${revisaoSistemas}</li>`,
            `\t\t\t<li><strong>História Patológica Pregressa: </strong>${historiaPatologicaPregressa}</li>`,
            `\t\t\t<li><strong>História Familiar: </strong>${historiaFamiliar}</li>`,
            `\t\t\t<li><strong>História Social: </strong>${historiaSocial}</li>`,
            "\t\t</ul>",
            "\t</li>",
            "\t<li>Exame físico",
            '\t\t<ul class="pl-5 mt-2 space-y-1 list-disc list-inside">',
            `\t\t\t<li><strong>Descrição: </strong>${descricaoExame}</li>`,
            `\t\t\t<li><strong>Altura: </strong>${altura}</li>`,
            `\t\t\t<li><strong>Peso: </strong>${peso}</li>`,
            `\t\t\t<li><strong>Perímetro cefálico: </strong>${perimetroCefalico}</li>`,
            "\t\t</ul>",
            "\t</li>",
            "\t<li>Exames complementares",
            '\t\t<ul class="pl-5 mt-2 space-y-1 list-disc list-inside">',
            `\t\t\t<li><strong>Descrição: </strong>${exameComplementarDescricao}</li>`,
            "\t\t</ul>",
            "\t</li>",
            "\t<li>Impressão e Conduta",
            '\t\t<ul class="pl-5 mt-2 space-y-1 list-disc list-inside">',
            `\t\t\t<li><strong>Descrição: </strong>${condutaDescricao}</li>`,
            "\t\t</ul>",
            "\t</li>",
            "</ol>",
        ].join("\n");
    };

    const copiarDadosParaConsultaAtual = (consulta) => {
        if (!consulta) {
            return;
        }

        const anamnese = consulta.anamnese || {};
        const exameFisico = consulta.exameFisico || {};
        const exameComplementar = consulta.exameComplementar || {};
        const conduta = consulta.conduta || {};
        const resumoCompletoDoHistorico = consulta.resumoCompletoDoHistorico
            ? normalizarResumoCompletoDoHistorico(
                  consulta.resumoCompletoDoHistorico,
              )
            : montarResumoCompletoHtml(consulta);

        resumoAtualizadoViaCopiarRef.current = true;
        if (resumoCompletoRef.current) {
            resumoCompletoRef.current.innerHTML = resumoCompletoDoHistorico;
        }

        setFormData({
            paciente: paciente,
            anamnese: {
                queixaPrincipal: formatarTexto(anamnese.queixaPrincipal),
                historiaDaMolestiaAtual: formatarTexto(
                    anamnese.historiaDaMolestiaAtual,
                ),
                revisaoDeSistemas: formatarTexto(anamnese.revisaoDeSistemas),
                historiaPatologicaPregressa: formatarTexto(
                    anamnese.historiaPatologicaPregressa,
                ),
                historiaFamiliar: formatarTexto(anamnese.historiaFamiliar),
                historiaSocial: formatarTexto(anamnese.historiaSocial),
            },
            exameFisico: {
                exameFisico: formatarTexto(exameFisico.exameFisico),
                altura: formatarComUnidade(exameFisico.altura, "cm"),
                peso: formatarComUnidade(exameFisico.peso, "kg"),
                perimetroCefalico: formatarComUnidade(
                    exameFisico.perimetroCefalico,
                    "cm",
                ),
            },
            exameComplementar: {
                exameComplementar: formatarTexto(
                    exameComplementar.exameComplementar,
                ),
            },
            conduta: {
                conduta: formatarTexto(conduta.conduta),
            },
            usuario: authResponseDTO,
            resumoCompletoDoHistorico,
        });
    };

    const tabs = [
        { id: 1, title: "Histórico de consultas" },
        { id: 2, title: "Anamnese" },
        { id: 3, title: "Exame físico" },
        { id: 4, title: "Exames complementares" },
        { id: 5, title: "Impressão e conduta" },
        { id: 6, title: "Observação" },
    ];
    const [activeTab, setActiveTab] = useState(tabs[0]);

    const formatarNumero = (inputValue) => {
        return (inputValue.replace(/\D/g, "") / 100).toFixed(2);
    };

    const handleNumeroChange = (event) => {
        const { name, value } = event.target;
        const numeroFormatado = formatarNumero(value);
        // setNumero(numeroFormatado);

        setFormData({
            ...formData,
            exameFisico: {
                ...formData.exameFisico,
                [name]: numeroFormatado,
            },
        });
    };

    return (
        <>
            {isModalOpen && (
                <div
                    id="novo-evento-modal"
                    tabIndex="-1"
                    aria-hidden="true"
                    className="b-block modal fixed inset-0 z-50 h-screen w-screen overflow-hidden"
                >
                    <div className="absolute inset-0 bg-black bg-opacity-50">
                        <div className="relative h-full w-full p-4 sm:p-6">
                            <div className="relative flex h-full w-full flex-col rounded-2xl bg-white shadow overflow-hidden dark:bg-gray-700">
                                    <div className="flex items-start justify-between p-4 border-b rounded-t dark:border-gray-600">
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                            Atendendo paciente
                                            <small>
                                                {" "}
                                                ({paciente.label},{" "}
                                                {paciente.idade}) - Tipo da
                                                consulta: {tipoConsulta}
                                            </small>
                                        </h3>
                                        <button
                                            onClick={onCloseModal}
                                            type="button"
                                            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                            data-modal-hide="novo-evento-modal"
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                aria-hidden="true"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 14 14"
                                            >
                                                <path
                                                    stroke="currentColor"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                                                />
                                            </svg>
                                            <span className="sr-only">
                                                Close modal
                                            </span>
                                        </button>
                                    </div>
                                    <div className="flex-1 min-h-0 p-4 overflow-y-auto overflow-x-hidden">
                                        <div className="grid h-full min-h-0 grid-cols-1 grid-rows-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.5fr)]">
                                            <div className="min-h-0 overflow-y-auto p-2">
                                                <h4 className="mb-4 text-sm font-medium text-gray-900 dark:text-white">
                                                    Linha do tempo do paciente
                                                </h4>
                                                <TimeLinePaciente
                                                    copiarDadosParaConsultaAtual={
                                                        copiarDadosParaConsultaAtual
                                                    }
                                                    consultasPorIdPaciente={
                                                        consultasPorIdPaciente
                                                    }
                                                    authResponseDTO={
                                                        authResponseDTO
                                                    }
                                                    isCopiarDados={true}
                                                />
                                            </div>
                                            <div className="min-h-0 overflow-y-auto p-2">
                                                <div
                                                    className={`w-full mb-4 border border-gray-200 rounded-lg bg-gray-50 shadow-sm dark:bg-gray-700 dark:border-gray-600 ${
                                                        resumoEmTelaCheia
                                                            ? "fixed inset-4 z-[60] flex flex-col"
                                                            : ""
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                                                        <div className="flex flex-wrap items-center divide-gray-200 sm:divide-x dark:divide-gray-600">
                                                            <div className="flex items-center space-x-1 rtl:space-x-reverse sm:pr-4">
                                                                <button
                                                                    type="button"
                                                                    title="Negrito"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        aplicarComandoResumo(
                                                                            "bold",
                                                                        );
                                                                    }}
                                                                    className="p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                                                                >
                                                                    <FontAwesomeIcon icon={faBold} />
                                                                    <span className="sr-only">
                                                                        Negrito
                                                                    </span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Itálico"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        aplicarComandoResumo(
                                                                            "italic",
                                                                        );
                                                                    }}
                                                                    className="p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                                                                >
                                                                    <FontAwesomeIcon icon={faItalic} />
                                                                    <span className="sr-only">
                                                                        Itálico
                                                                    </span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Sublinhado"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        aplicarComandoResumo(
                                                                            "underline",
                                                                        );
                                                                    }}
                                                                    className="p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                                                                >
                                                                    <FontAwesomeIcon icon={faUnderline} />
                                                                    <span className="sr-only">
                                                                        Sublinhado
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            <div className="flex flex-wrap items-center space-x-1 rtl:space-x-reverse sm:pl-4">
                                                                <button
                                                                    type="button"
                                                                    title="Lista"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        aplicarComandoResumo(
                                                                            "insertUnorderedList",
                                                                        );
                                                                    }}
                                                                    className="p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faListUl
                                                                        }
                                                                    />
                                                                    <span className="sr-only">
                                                                        Lista
                                                                    </span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Lista numerada"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        aplicarComandoResumo(
                                                                            "insertOrderedList",
                                                                        );
                                                                    }}
                                                                    className="p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faListOl
                                                                        }
                                                                    />
                                                                    <span className="sr-only">
                                                                        Lista numerada
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            title="Tela cheia"
                                                            onClick={() =>
                                                                setResumoEmTelaCheia(
                                                                    (valorAtual) =>
                                                                        !valorAtual,
                                                                )
                                                            }
                                                            className="p-2 text-gray-500 rounded cursor-pointer sm:ml-auto hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                                                        >
                                                            <svg
                                                                className="w-5 h-5"
                                                                aria-hidden="true"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="24"
                                                                height="24"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    stroke="currentColor"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M8 4H4m0 0v4m0-4 5 5m7-5h4m0 0v4m0-4-5 5M8 20H4m0 0v-4m0 4 5-5m7 5h4m0 0v-4m0 4-5-5"
                                                                />
                                                            </svg>
                                                            <span className="sr-only">
                                                                Tela cheia
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div
                                                        className={`px-4 py-2 bg-white rounded-b-lg dark:bg-gray-800 ${
                                                            resumoEmTelaCheia
                                                                ? "flex-1 min-h-0 overflow-y-auto"
                                                                : ""
                                                        }`}
                                                    >
                                                        <label
                                                            htmlFor="resumo-completo"
                                                            className="sr-only"
                                                        >
                                                            Resumo completo do
                                                            histórico
                                                        </label>
                                                        <div
                                                            id="resumo-completo"
                                                            className={`block w-full px-0 text-sm text-gray-900 bg-white border-0 focus:ring-0 focus:outline-none dark:bg-gray-800 dark:text-white [&_ol]:!list-decimal [&_ol]:!list-outside [&_ol]:!pl-6 [&_ul]:!list-disc [&_ul]:!list-outside [&_ul]:!ml-0 [&_ul]:!pl-6 [&_li]:my-1 ${
                                                                resumoEmTelaCheia
                                                                    ? "h-full min-h-[20rem] overflow-y-auto"
                                                                    : "min-h-[20rem]"
                                                            }`}
                                                            contentEditable
                                                            suppressContentEditableWarning
                                                            role="textbox"
                                                            aria-multiline="true"
                                                            ref={resumoCompletoRef}
                                                            onInput={
                                                                handleResumoHtmlInput
                                                            }
                                                            onKeyDown={
                                                                handleResumoKeyDown
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <div
                                        className="p-6 space-y-6 overflow-y-auto"
                                        style={{ height: "60vh" }}
                                    >
                                        <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
                                            <ul
                                                className="flex flex-wrap -mb-px text-sm font-medium text-center"
                                                id="myTab"
                                                data-tabs-toggle="#myTabContent"
                                                role="tablist"
                                            >
                                                {tabs.map((tab) => (
                                                    <li
                                                        key={tab.id}
                                                        onClick={() =>
                                                            setActiveTab(tab)
                                                        }
                                                        className={`mr-2`}
                                                    >
                                                        <button
                                                            className={`inline-block p-4 border-b-2 ${
                                                                activeTab.id ===
                                                                tab.id
                                                                    ? "text-blue-600"
                                                                    : "border-transparent"
                                                            } rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300`}
                                                        >
                                                            {tab.title}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div id="myTabContent">
                                            <div
                                                className={`${
                                                    activeTab.id === 1
                                                        ? ""
                                                        : "hidden"
                                                }p-4 rounded-lg bg-gray-100 dark:bg-gray-800`}
                                            >
                                                <div
                                                    className={`${
                                                        activeTab.id === 1
                                                            ? ""
                                                            : "hidden"
                                                    } text-sm text-gray-500 dark:text-gray-400`}
                                                >
                                                    <TimeLinePaciente
                                                        copiarDadosParaConsultaAtual={
                                                            copiarDadosParaConsultaAtual
                                                        }
                                                        consultasPorIdPaciente={
                                                            consultasPorIdPaciente
                                                        }
                                                        authResponseDTO={
                                                            authResponseDTO
                                                        }
                                                        isCopiarDados={true}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className={`${
                                                    activeTab.id === 2
                                                        ? ""
                                                        : "hidden"
                                                }p-4 rounded-lg bg-gray-100 dark:bg-gray-800`}
                                            >
                                                <div
                                                    className={
                                                        activeTab.id === 2
                                                            ? ""
                                                            : "hidden"
                                                    }
                                                >
                                                    <label
                                                        for="queixa-principal"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        Queixa principal (QP)
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleAnamneseChange
                                                        }
                                                        name="queixaPrincipal"
                                                        value={
                                                            formData.anamnese
                                                                .queixaPrincipal
                                                        }
                                                        id="queixa-principal"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a queixa principal..."
                                                    ></textarea>

                                                    <label
                                                        for="historia-molestia-atual"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        História da Moléstia
                                                        Atual (HMA)
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleAnamneseChange
                                                        }
                                                        name="historiaDaMolestiaAtual"
                                                        value={
                                                            formData.anamnese
                                                                .historiaDaMolestiaAtual
                                                        }
                                                        id="historia-molestia-atual"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a história da moléstia atual..."
                                                    ></textarea>

                                                    <label
                                                        for="revisao-sistemas"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        Revisão de Sistemas
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleAnamneseChange
                                                        }
                                                        name="revisaoDeSistemas"
                                                        value={
                                                            formData.anamnese
                                                                .revisaoDeSistemas
                                                        }
                                                        id="revisao-sistemas"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a revisão de sistemas..."
                                                    ></textarea>

                                                    <label
                                                        for="historia-patologica-pregressa"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        História Patológica
                                                        Pregressa
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleAnamneseChange
                                                        }
                                                        name="historiaPatologicaPregressa"
                                                        value={
                                                            formData.anamnese
                                                                .historiaPatologicaPregressa
                                                        }
                                                        id="historia-patologica-pregressa"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a história patológica pregressa..."
                                                    ></textarea>

                                                    <label
                                                        for="historia-familiar"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        História Familiar
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleAnamneseChange
                                                        }
                                                        name="historiaFamiliar"
                                                        value={
                                                            formData.anamnese
                                                                .historiaFamiliar
                                                        }
                                                        id="historia-familiar"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a história familiar..."
                                                    ></textarea>

                                                    <label
                                                        for="historia-social"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        História Social
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleAnamneseChange
                                                        }
                                                        name="historiaSocial"
                                                        value={
                                                            formData.anamnese
                                                                .historiaSocial
                                                        }
                                                        id="historia-social"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a história social..."
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div
                                                className={`${
                                                    activeTab.id === 3
                                                        ? ""
                                                        : "hidden"
                                                }p-4 rounded-lg bg-gray-100 dark:bg-gray-800`}
                                            >
                                                <div
                                                    className={`${
                                                        activeTab.id === 3
                                                            ? ""
                                                            : "hidden"
                                                    } text-sm text-gray-500 dark:text-gray-400`}
                                                >
                                                    <label
                                                        for="exame-fisico"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        Exame físico
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleExameFisicoChange
                                                        }
                                                        name="exameFisico"
                                                        value={
                                                            formData.exameFisico
                                                                .exameFisico
                                                        }
                                                        id="exame-fisico"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui o exame físico do paciente..."
                                                    ></textarea>
                                                    <div className="grid md:grid-cols-6 md:gap-6">
                                                        <div className="relative z-0 w-full group">
                                                            <label
                                                                for="exame-fisico-altura"
                                                                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                            >
                                                                Altura
                                                            </label>
                                                            <div className="flex">
                                                                <input
                                                                    type="text"
                                                                    pattern="\d*"
                                                                    maxLength={
                                                                        6
                                                                    }
                                                                    name="altura"
                                                                    id="exame-fisico-altura"
                                                                    className="rounded-none rounded-s-lg bg-gray-50 border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                                    placeholder="Ex.: 42.5"
                                                                    value={
                                                                        formData
                                                                            .exameFisico
                                                                            .altura
                                                                    }
                                                                    onChange={
                                                                        handleNumeroChange
                                                                    }
                                                                />
                                                                <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border rounded-e-md border-gray-300 rounded-s-0 dark:bg-gray-600 dark:text-gray-400 dark:border-gray-600">
                                                                    cm
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="relative z-0 w-full group">
                                                            <label
                                                                for="exame-fisico-peso"
                                                                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                            >
                                                                Peso
                                                            </label>
                                                            <div className="flex">
                                                                <input
                                                                    type="text"
                                                                    pattern="\d*"
                                                                    maxLength={
                                                                        5
                                                                    }
                                                                    name="peso"
                                                                    id="exame-fisico-peso"
                                                                    className="rounded-none rounded-s-lg bg-gray-50 border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                                    placeholder="Ex.: 42.5"
                                                                    value={
                                                                        formData
                                                                            .exameFisico
                                                                            .peso
                                                                    }
                                                                    onChange={
                                                                        handleNumeroChange
                                                                    }
                                                                />
                                                                <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border rounded-e-md border-gray-300 rounded-s-0 dark:bg-gray-600 dark:text-gray-400 dark:border-gray-600">
                                                                    kg
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="relative z-0 w-full group">
                                                            <label
                                                                for="exame-fisico-perimetroCefalico"
                                                                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                            >
                                                                Perímetro
                                                                cefálico
                                                            </label>
                                                            <div className="flex">
                                                                <input
                                                                    type="text"
                                                                    pattern="\d*"
                                                                    maxLength={
                                                                        5
                                                                    }
                                                                    name="perimetroCefalico"
                                                                    id="exame-fisico-perimetroCefalico"
                                                                    className="rounded-none rounded-s-lg bg-gray-50 border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                                    placeholder="Ex.: 42.5"
                                                                    value={
                                                                        formData
                                                                            .exameFisico
                                                                            .perimetroCefalico
                                                                    }
                                                                    onChange={
                                                                        handleNumeroChange
                                                                    }
                                                                />
                                                                <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border rounded-e-md border-gray-300 rounded-s-0 dark:bg-gray-600 dark:text-gray-400 dark:border-gray-600">
                                                                    cm
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {consultasParaGraficosPorIdPaciente &&
                                                    consultasParaGraficosPorIdPaciente !==
                                                        null ? (
                                                        <Graficos
                                                            consultasDoPaciente={
                                                                consultasParaGraficosPorIdPaciente
                                                            }
                                                            permiteSelecionarPaciente={
                                                                false
                                                            }
                                                            paciente={paciente}
                                                            authResponseDTO={
                                                                authResponseDTO
                                                            }
                                                            token={token}
                                                            perimetroCefalico={
                                                                formData
                                                                    .exameFisico
                                                                    .perimetroCefalico
                                                            }
                                                            altura={
                                                                formData
                                                                    .exameFisico
                                                                    .altura
                                                            }
                                                            peso={
                                                                formData
                                                                    .exameFisico
                                                                    .peso
                                                            }
                                                            isModalDeAtendimento={
                                                                true
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="mt-6 text-center">
                                                            Paciente não possui
                                                            dados essenciais
                                                            para que os gráficos
                                                            sejam exibidos.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div
                                                className={`${
                                                    activeTab.id === 4
                                                        ? ""
                                                        : "hidden"
                                                }p-4 rounded-lg bg-gray-100 dark:bg-gray-800`}
                                            >
                                                <div
                                                    className={`${
                                                        activeTab.id === 4
                                                            ? ""
                                                            : "hidden"
                                                    } text-sm text-gray-500 dark:text-gray-400`}
                                                >
                                                    <label
                                                        for="exames-complementares"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        Exames Complementares
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleExameComplementarChange
                                                        }
                                                        name="exameComplementar"
                                                        value={
                                                            formData
                                                                .exameComplementar
                                                                .exameComplementar
                                                        }
                                                        id="exames-complementares"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui os exames complementares..."
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div
                                                className={`${
                                                    activeTab.id === 5
                                                        ? ""
                                                        : "hidden"
                                                }p-4 rounded-lg bg-gray-100 dark:bg-gray-800`}
                                            >
                                                <div
                                                    className={`${
                                                        activeTab.id === 5
                                                            ? ""
                                                            : "hidden"
                                                    } text-sm text-gray-500 dark:text-gray-400`}
                                                >
                                                    <label
                                                        for="impressao-conduta"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        Impressão e Conduta
                                                    </label>
                                                    <textarea
                                                        onChange={
                                                            handleCondutaChange
                                                        }
                                                        name="conduta"
                                                        value={
                                                            formData.conduta
                                                                .conduta
                                                        }
                                                        id="impressao-conduta"
                                                        rows="4"
                                                        className="mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                        placeholder="Escreva aqui a impressão e conduta..."
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div
                                                className={`${
                                                    activeTab.id === 6
                                                        ? ""
                                                        : "hidden"
                                                }p-4 rounded-lg bg-gray-100 dark:bg-gray-800`}
                                            >
                                                <div
                                                    className={`${
                                                        activeTab.id === 6
                                                            ? ""
                                                            : "hidden"
                                                    } text-sm text-gray-500 dark:text-gray-400`}
                                                >
                                                    <label
                                                        for="observacao"
                                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                                    >
                                                        Observação
                                                    </label>
                                                    <textarea
                                                        disabled
                                                        name="observacao"
                                                        value={observacao}
                                                        id="observacao"
                                                        rows="4"
                                                        className="no-resize mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}
                                    <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b dark:border-gray-600">
                                        <button
                                            onClick={onCloseModal}
                                            type="button"
                                            className="text-white bg-gradient-to-br from-pink-500 to-orange-400 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-pink-200 dark:focus:ring-pink-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                            data-modal-hide="novo-evento-modal"
                                        >
                                            Fechar
                                        </button>
                                        <button
                                            onClick={salvarConsulta}
                                            type="button"
                                            className="text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-cyan-300 dark:focus:ring-cyan-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                        >
                                            Salvar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                </div>
            )}
        </>
    );
};

export default FichaDeAtendimentoModal;
